package com.partejar.app.core.network

import com.partejar.app.core.session.CurrentWorkspaceRepository
import com.partejar.app.core.session.SessionLocalStateCleaner
import com.partejar.app.core.session.SessionTokenStore
import com.partejar.app.core.session.SessionTokens
import com.partejar.app.core.session.SessionTemporarilyUnavailableException
import io.ktor.client.HttpClient
import io.ktor.client.call.body
import io.ktor.client.plugins.auth.providers.BearerTokens
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.http.ContentType
import io.ktor.http.HttpStatusCode
import io.ktor.http.contentType
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import kotlinx.serialization.Serializable

class SessionRefreshCoordinator(
    private val tokenStore: SessionTokenStore,
    private val apiConfig: ApiConfig,
    private val currentWorkspaceRepository: CurrentWorkspaceRepository? = null,
    private val sessionLocalStateCleaner: SessionLocalStateCleaner? = null
) {
    private val refreshMutex = Mutex()

    suspend fun refresh(
        client: HttpClient,
        previousTokens: BearerTokens?,
        markAsRefreshRequest: HttpRequestBuilder.() -> Unit
    ): BearerTokens? = refreshMutex.withLock {
        val currentTokens = tokenStore.current() ?: return null
        if (previousTokens?.refreshToken != null && previousTokens.refreshToken != currentTokens.refreshToken) {
            return currentTokens.toBearerTokens()
        }

        val response = client.post(apiConfig.endpoint("/auth/refresh")) {
            markAsRefreshRequest()
            contentType(ContentType.Application.Json)
            setBody(RefreshTokenRequest(currentTokens.refreshToken))
        }
        when (response.status) {
            HttpStatusCode.OK -> response.body<RefreshTokenResponse>().toTokens().also(tokenStore::save).toBearerTokens()
            HttpStatusCode.Unauthorized -> {
                sessionLocalStateCleaner?.clear() ?: run {
                    tokenStore.clear()
                    currentWorkspaceRepository?.clear()
                }
                null
            }
            else -> throw SessionTemporarilyUnavailableException()
        }
    }

    private fun SessionTokens.toBearerTokens() = BearerTokens(accessToken, refreshToken)
}

@Serializable
private data class RefreshTokenRequest(val refreshToken: String)

@Serializable
private data class RefreshTokenResponse(
    val accessToken: String,
    val refreshToken: String
) {
    fun toTokens() = SessionTokens(accessToken, refreshToken)
}
